// Phase 15 Plan 08 Task 3 — Single leaderboard row.
//
// Renders one entry from a LeaderboardResponse.entries array:
//   - rank (1-based, tabular-num, Display 28px Fredoka)
//   - display_name
//   - mu (Glicko-2 storage scale, 1500-centered — rounded to integer for
//     display)
// Tap → navigates to /profile/{user_uuid} (Plan 14 router pattern).
//
// `isSelf` flag, when true, renders the row with a Sun-Yellow 4px left edge
// — visual cue per 15-UI-SPEC §"Leaderboard Interaction Contracts" item 3.
// When the caller IS in the top 100, the inline row is highlighted instead
// of a separate sticky surface; when they are NOT, the sticky row at the
// bottom of LeaderboardScreen uses the same isSelf=true visual treatment.

import type { JSX } from 'react'
import { FormattedNumber } from 'react-intl'

import { useRouter } from '../../router/router'
import { Avatar } from '../components/Avatar'
import { Icon } from '../components/Icon'

// Folk leaderboard: top-3 ranks render a single take-two medal line-icon
// (prototype LbRow uses ONE <Icon name="medal"> tinted gold/silver/bronze by
// rank — not three distinct emoji).
const MEDAL_COLOR_BY_RANK: Readonly<Record<number, string>> = {
  1: 'var(--color-accent-gold)',
  2: 'var(--color-medal-silver)',
  3: 'var(--color-medal-bronze)',
}

export interface LeaderboardRowProps {
  readonly rank: number
  readonly userUuid: string
  readonly displayName: string
  readonly mu: number
  readonly isSelf?: boolean
  /**
   * When true, renders with position:sticky bottom for the
   * sub-top-100 "Your rank" anchor. Adds extra z-index + shadow.
   */
  readonly sticky?: boolean
}

export function LeaderboardRow(props: LeaderboardRowProps): JSX.Element {
  const {
    rank,
    userUuid,
    displayName,
    mu,
    isSelf = false,
    sticky = false,
  } = props
  const router = useRouter()
  const medalColor = MEDAL_COLOR_BY_RANK[rank]
  const initial = displayName.slice(0, 1).toUpperCase()

  return (
    <button
      type="button"
      className="row"
      onClick={() => router.navigate(`/profile/${userUuid}`)}
      data-testid={`leaderboard-row-${rank}`}
      data-row-self={isSelf ? 'true' : 'false'}
      data-row-sticky={sticky ? 'true' : 'false'}
      data-user-uuid={userUuid}
      style={{
        // `.row` is applied to a <button> here — reset the UA border (the
        // recipe owns bg/radius/shadow but not button chrome).
        border: 'none',
        textAlign: 'left',
        position: sticky ? 'sticky' : undefined,
        bottom: sticky ? 'var(--space-2)' : undefined,
        zIndex: sticky ? 50 : undefined,
        // Folk "you" cue (prototype LbRow): warm ember ring over the card drop;
        // sticky rows lift onto the warmer pop shadow. Non-self/non-sticky rows
        // inherit the plain .row card shadow.
        boxShadow: isSelf
          ? `${sticky ? 'var(--shadow-pop)' : 'var(--shadow-card)'}, 0 0 0 2.5px var(--color-player-a)`
          : sticky
            ? 'var(--shadow-pop)'
            : undefined,
        fontFamily: 'var(--font-family-body)',
        color: 'var(--color-text-primary)',
      }}
    >
      <span
        style={{
          flex: '0 0 var(--space-6)',
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-16)',
          fontVariantNumeric: 'tabular-nums',
          color: isSelf
            ? 'var(--color-player-a)'
            : 'var(--color-text-tertiary)',
          textAlign: 'center',
        }}
      >
        {medalColor ? (
          <>
            <Icon name="medal" size={22} color={medalColor} />
            <span className="sr-only">
              <FormattedNumber value={rank} />
            </span>
          </>
        ) : (
          <FormattedNumber value={rank} />
        )}
      </span>
      <Avatar
        initial={initial}
        size={42}
        color={isSelf ? 'var(--color-player-a)' : 'var(--color-player-b)'}
      />
      <span
        data-testid={`leaderboard-row-name-${rank}`}
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          fontFamily: 'var(--font-family-display)',
          fontWeight: 600,
          fontSize: 'var(--font-size-16)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {displayName}
      </span>
      <span
        data-testid={`leaderboard-row-elo-${rank}`}
        style={{
          flex: '0 0 auto',
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-16)',
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--color-text-secondary)',
        }}
      >
        <FormattedNumber value={Math.round(mu)} />
      </span>
    </button>
  )
}
